import { Box, Button, FormControl, HorizontalStack, Modal, Text, Textarea } from '@lego/klik-ui';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import {
  type ImageScanFinding,
  type ImageTagApplication,
  useDismissVulnerabilityFinding,
  useReopenVulnerabilityFinding,
} from '@/hooks/repository';

const MIN_REASON_LENGTH = 10;

type Props = {
  finding: ImageScanFinding;
  applications: ImageTagApplication[];
  workspaceId: number;
  repositoryId: number;
  imageTag: string;
  onClose: () => void;
};

export const DismissalDialog = ({
  finding,
  applications,
  workspaceId,
  repositoryId,
  imageTag,
  onClose,
}: Props) => {
  const dismissedApplicationId = finding.dismissals?.find((dismissal) =>
    applications.some((application) => application.id === dismissal.applicationId)
  )?.applicationId;

  const [selectedApplicationId, setSelectedApplicationId] = useState<number>(
    dismissedApplicationId ?? applications[0]?.id
  );
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dismissFinding = useDismissVulnerabilityFinding(workspaceId, repositoryId, imageTag);
  const reopenFinding = useReopenVulnerabilityFinding(workspaceId, repositoryId, imageTag);

  const selectedDismissal = finding.dismissals?.find(
    (dismissal) => dismissal.applicationId === selectedApplicationId
  );

  const canSubmitDismissal =
    Boolean(finding.vulnerabilityId && finding.packageName && selectedApplicationId) &&
    reason.trim().length >= MIN_REASON_LENGTH &&
    !isSubmitting;

  const handleDismiss = async () => {
    setReasonTouched(true);
    if (!finding.vulnerabilityId || !finding.packageName || !selectedApplicationId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await dismissFinding({
        applicationId: selectedApplicationId,
        vulnerabilityId: finding.vulnerabilityId,
        packageName: finding.packageName,
        reason: reason.trim(),
      });
      onClose();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!finding.vulnerabilityId || !finding.packageName || !selectedApplicationId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await reopenFinding({
        applicationId: selectedApplicationId,
        vulnerabilityId: finding.vulnerabilityId,
        packageName: finding.packageName,
      });
      onClose();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Dismiss Vulnerability Finding</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <Text fontWeight="bold">{finding.vulnerabilityId}</Text>
          <Text mb={4}>{finding.packageName}</Text>
          <FormControl isRequired>
            <FormControl.Label>Application</FormControl.Label>
            <Box
              as="select"
              borderColor="gray.300"
              borderRadius="md"
              borderWidth="1px"
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                setSelectedApplicationId(Number.parseInt(event.target.value, 10))
              }
              p={2}
              value={selectedApplicationId}
              width="100%"
            >
              {applications.map((application) => (
                <option key={application.id} value={application.id}>
                  {application.name}
                </option>
              ))}
            </Box>
          </FormControl>
          {selectedDismissal ? (
            <Box mt={4}>
              <Text fontWeight="bold">Dismissal reason</Text>
              <Text>{selectedDismissal.reason}</Text>
            </Box>
          ) : (
            <FormControl
              isInvalid={reasonTouched && reason.trim().length < MIN_REASON_LENGTH}
              isRequired
              mt={4}
            >
              <FormControl.Label>Reason</FormControl.Label>
              <Textarea
                onBlur={() => setReasonTouched(true)}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setReason(event.target.value)
                }
                placeholder="Explain why this finding can be dismissed"
                value={reason}
              />
              {reasonTouched && reason.trim().length < MIN_REASON_LENGTH && (
                <FormControl.ErrorText>
                  Reason is required (at least {MIN_REASON_LENGTH} characters)
                </FormControl.ErrorText>
              )}
            </FormControl>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            {selectedDismissal ? (
              <Button isLoading={isSubmitting} onClick={handleReopen} variant="warning">
                Re-open
              </Button>
            ) : (
              <Button
                isDisabled={!canSubmitDismissal}
                isLoading={isSubmitting}
                onClick={handleDismiss}
              >
                Dismiss
              </Button>
            )}
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
